import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogContent,
  FormControlLabel,
  Grid,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Typography
} from "@mui/material";
import React, { ChangeEvent, useCallback, useContext, useEffect, useRef, useState } from "react";

import { SessionContext } from "../../../../Context/SessionContext";
import { UserContext } from "../../../../Context/UserContext";
import { Customer, EnhancedCustomer } from "../../../../Models/Customer";
import { CustomerNote, CustomerNoteType, EnhancedCustomerNote } from "../../../../Models/CustomerNote";
import { LeadResult } from "../../../../Models/Lead";
import { StepId, Steps } from "../../../../Types/Step";
import { LeadExternalSystem, LeadResultType } from "../../../../Utils/Enums";
import { Error } from "../../../../Utils/Errors";
import OpsEndpointPathsUtility from "../../../../Utils/OpsEndpointPathsUtility";
import { useApi } from "../../../../Utils/useApi";
import SessionContextContactOverrideModal from "../../../../V2/Playbook/Search/Compositions/SessionContextContactOverrideModal";
import useGetCustomer from "../../../../V2/Playbook/Search/Hooks/UseGetCustomer";
import AlertGridItem from "../../../Helpers/AlertGridItem";
import Modal from "../../../Helpers/Modal";
import CustomerTable from "./SearchCustomerTable";
import SearchForm from "./SearchForm";

const abandonedLastName = process.env.REACT_APP_LAST_NAME_RESET || "ExistingGuest";

export interface SearchParams {
  anyFlag: boolean;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

const Search: React.FC = () => {
  const { areasOfInterest, centers, leadResultTypes } = useContext(UserContext);
  const [badLeadResult] = useState(leadResultTypes.find(lrt => lrt.name === LeadResultType.BadLead));
  const {
    customer,
    isSearchComplete,
    lead,
    note,
    overrideEmail,
    phoneNumberErrors,
    previewCustomer,
    selectedCustomer,
    setNote,
    setIsSearchComplete,
    setLeadResult,
    setOverrideEmail,
    setSelectedCustomer,
    setStep,
    setPreviewCustomer,
    helpers: { endSession, loadFromLocal, saveToLocal }
  } = useContext(SessionContext);
  // on unmount, clear the preview customer
  useEffect(() => () => setPreviewCustomer(undefined), []);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [deleteNote, setDeleteNote] = useState<CustomerNote>();
  const [leadResult, setPostLeadResult] = useState<LeadResult>();
  const [isModalOpen, setModalOpen] = useState(false);
  const [preserveRibbon, setPreserveRibbon] = useState(false);
  const [preserveNote, setPreserveNote] = useState(false);
  const [resetCustomer, setResetCustomer] = useState<Customer>();
  const [searchCustomers, setSearchCustomers] = useState(
    loadFromLocal<Customer[]>("searchCustomers") || lead.customers
  );
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const resetCustomerCallbackRef = useRef<() => void>();
  const [isContactOverrideOpen, setIsContactOverrideOpen] = useState(false);

  const { response: customerSearchResponse, isLoading: customerLoading } = useApi<Customer[]>({
    url: `Legacy/V0/Contacts/${lead.id}/Customers`,
    config: { params: searchParams },
    valid: !!searchParams,
    error: Error.GetCustomers
  });
  useEffect(() => {
    if (!customerSearchResponse) return;
    saveToLocal(customerSearchResponse, "searchCustomers");
    setSearchCustomers(customerSearchResponse);
    setSearchParams(undefined);
  }, [customerSearchResponse]);

  const { result: customerResponse, execute: setRequest } = useGetCustomer({
    areaOfInterestOptions: areasOfInterest,
    centerOptions: centers
  });
  useEffect(() => {
    if (previewCustomer?.shallow && previewCustomer?.id)
      setRequest({ customerId: previewCustomer.id, contactId: lead.id });
  }, [previewCustomer]);
  useEffect(() => {
    if (customerResponse) {
      // update the search customers
      setSearchCustomers(oldCustomers => {
        const customers = [...oldCustomers];
        customers[customers.findIndex(c => c.id === customerResponse.id)] = customerResponse;
        saveToLocal(customers, "searchCustomers");
        return customers;
      });
      // if previewing, update preview customer
      setPreviewCustomer(oldPreview => {
        if (oldPreview?.id !== customerResponse.id || !oldPreview.shallow) return oldPreview;
        return new EnhancedCustomer({
          ...customerResponse,
          center: centers.find(c => c.id === customerResponse.centerId)
        });
      });
    }
  }, [customerResponse]);

  const {
    response: resetCustomerResponse,
    isLoading: resetCustomerLoading,
    error: resetCustomerError
  } = useApi<unknown, Customer>({
    method: "put",
    url: OpsEndpointPathsUtility.aspirePutCustomerUrl(lead.id, resetCustomer?.id || ""),
    data: resetCustomer,
    valid: !!resetCustomer,
    error: Error.PutCustomerReset
  });
  const {
    response: deleteNoteResponse,
    isLoading: deleteNoteLoading,
    error: deleteNoteError
  } = useApi({
    method: "delete",
    url: `Legacy/V0/contacts/${lead.id}/customers/${resetCustomer?.id}/notes/${deleteNote?.id}`,
    valid: !!deleteNote && !!resetCustomer,
    error: Error.DeleteNote
  });
  useEffect(() => {
    if ((resetCustomerResponse || resetCustomerError) && (deleteNoteResponse || deleteNoteError || !deleteNote))
      resetCustomerCallbackRef.current && resetCustomerCallbackRef.current();
  }, [resetCustomerResponse, resetCustomerError, deleteNoteResponse, deleteNoteError, deleteNote]);

  const {
    response: leadResultResponse,
    error: leadResultError,
    isLoading: leadResultLoading
  } = useApi<LeadResult, LeadResult>({
    method: leadResult?.id ? "put" : "post",
    url: `Legacy/V0/Contacts/${lead.id}/Result`,
    data: leadResult as LeadResult,
    error: leadResult?.id ? Error.PutLeadResult : Error.PostLeadResult,
    valid: !!leadResult
  });
  // ON PUT/POST LEAD RESULT SUCCESS
  useEffect(() => {
    if (leadResultResponse) {
      setLeadResult(leadResultResponse);
      endSession();
    }
  }, [leadResultResponse, endSession, setLeadResult]);
  // ON PUT/POST LEAD RESULT ERROR
  useEffect(() => {
    const leadResult = leadResultError?.response?.data?.data as LeadResult | undefined;
    if (leadResult) {
      setLeadResult(leadResult);
      endSession();
    }
  }, [leadResultError, endSession, setLeadResult]);

  const handleNext = (oldRibbonCustomer?: Customer) => {
    setSelectedCustomer(oldRibbonCustomer);
    setIsSearchComplete(true);
    setModalOpen(false);
    setStep(Steps[StepId.Intro]);
  };
  const handleBadLead = () =>
    setPostLeadResult({
      id: lead.result?.id,
      type: badLeadResult?.id ?? 0,
      customerId: null,
      appointmentId: null,
      opportunityId: null,
      notOfferedProcedures: {},
      notes: null,
      disqualifyingReasons: {}
    });
  const handleConfirm = () => {
    // if first time completing search, short-circuit proceed
    if (!isSearchComplete) return handleNext();
    resetCustomerCallbackRef.current = () => handleNext(preserveRibbon ? customer : undefined);
    // delete the note on the old guest
    if (note.id) setDeleteNote({ ...note });
    if (previewCustomer?.centerId || previewCustomer?.center?.id || customer?.centerId)
      setNote(
        new EnhancedCustomerNote({
          text: preserveNote ? note.text : "",
          centerId: previewCustomer?.center?.id || previewCustomer?.centerId || customer?.centerId || "",
          type: CustomerNoteType.Generic
        })
      );
    // IF we proceeded with an existing guest, reset it
    if (selectedCustomer) setResetCustomer(selectedCustomer);
    // ELSE IF a new customer will be abandoned, modify it
    else if (customer.id) setResetCustomer({ ...customer, lastName: abandonedLastName });
    // ELSE execute the callback now to proceed
    else resetCustomerCallbackRef.current();
  };
  const handleProceed = useCallback(() => {
    const showOverrideContact =
      (lead.externalSystem === LeadExternalSystem.WebForm ||
        lead.externalSystem === LeadExternalSystem.InContactChat) &&
      ((previewCustomer?.firstName && previewCustomer?.firstName !== lead.firstName) ||
        (previewCustomer?.lastName && previewCustomer?.lastName !== lead.lastName) ||
        (previewCustomer?.email && lead.email !== previewCustomer.email) ||
        (previewCustomer?.phoneNumber && lead.phoneNumber !== previewCustomer.phoneNumber));

    if (isSearchComplete || shouldConfirmEmail) {
      setModalOpen(true);
      return;
    }

    if (showOverrideContact) {
      setIsContactOverrideOpen(true);
      return;
    }

    if (!shouldConfirmEmail && !showOverrideContact) {
      handleNext();
    }
  }, [isSearchComplete, lead, previewCustomer]);
  // if this is an email lead, and it differs from a selected preview customer, flag email to confirm
  const shouldConfirmEmail =
    lead.externalSystem === LeadExternalSystem.Email && previewCustomer?.email && lead.email !== previewCustomer.email;
  const phoneErrors = Object.entries(phoneNumberErrors).filter(([, value]) => value);

  return (
    <Paper>
      {leadResultLoading ? (
        <Grid container item xs alignItems="center" justifyContent="center">
          <CircularProgress sx={{ padding: 5 }} />
        </Grid>
      ) : (
        <Grid container direction="column" spacing={2} p={2}>
          <Grid item>
            <Typography variant="body1" align="center">
              Please select the best matching Guest record from the list.
            </Typography>
          </Grid>
          <Grid item>
            <CustomerTable
              customers={searchCustomers}
              disabled={customerLoading}
              setPreviewCustomer={setPreviewCustomer}
              selectedCustomerId={previewCustomer?.id || undefined}
            />
          </Grid>
          <AlertGridItem severity="info" in={Boolean(phoneErrors.length)} spacing={2}>
            {`${phoneErrors.length} phone ${
              phoneErrors.length > 1 ? "numbers are" : "number is"
            } not allowed. See the ribbon for details.`}
          </AlertGridItem>
          <Grid item>
            <SearchForm loading={customerLoading} setSearchParams={setSearchParams} />
          </Grid>
          <Grid item>
            <Grid container justifyContent="space-between">
              <Grid item>
                <Button
                  color="secondary"
                  onClick={() => {
                    setPreviewCustomer(undefined);
                    setModalOpen(true);
                  }}
                >
                  No Match, Create New Guest
                </Button>
              </Grid>
              {!isSearchComplete && (
                <>
                  <Grid item>
                    <Button onClick={e => setAnchorEl(e.currentTarget)} color="error">
                      Bad Lead
                    </Button>
                  </Grid>
                  <Popover
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                  >
                    <Box p={2}>
                      <Grid container direction="column" spacing={1}>
                        <Grid item>
                          <Typography variant="body2">Disposition as a bad lead and end the session?</Typography>
                        </Grid>
                        <Grid container item xs justifyContent="center">
                          <Button onClick={handleBadLead}>Confirm</Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Popover>
                </>
              )}
              <Grid item>
                {previewCustomer?.shallow ? (
                  <CircularProgress />
                ) : (
                  <Button onClick={() => handleProceed()} disabled={!previewCustomer}>
                    Proceed
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!isSearchComplete && previewCustomer && (
        <SessionContextContactOverrideModal
          open={isContactOverrideOpen}
          handleClose={() => setIsContactOverrideOpen(false)}
          handleConfirm={handleConfirm}
        />
      )}
      <Modal
        open={isModalOpen}
        handleClose={() => setModalOpen(false)}
        handleConfirm={handleConfirm}
        confirmDisabled={resetCustomerLoading || deleteNoteLoading}
        title={isSearchComplete ? "Switch Guest" : "New Guest"}
      >
        <DialogContent>
          {!isSearchComplete && !previewCustomer && (
            <Typography variant="body1">
              You have selected to create a new Guest. Please be absolutely sure that none of the possible matches are
              appropriate.
            </Typography>
          )}
          {(isSearchComplete && (resetCustomerLoading || deleteNoteLoading) && (
            <Grid container justifyContent="center">
              <CircularProgress />
            </Grid>
          )) ||
            (isSearchComplete && (
              <Grid container direction="column" spacing={2}>
                {customer.id && (
                  <Grid item>
                    <Typography variant="body1">
                      {`You are already working with a guest. If you switch to a different one, all changes to the previous guest will be reset${
                        note.id ? " and the customer note will be deleted." : "."
                      } Please be absolutely sure that you wish to continue.`}
                    </Typography>
                  </Grid>
                )}
                <Grid item>
                  <Typography variant="body1">
                    {`Options for ${
                      previewCustomer
                        ? `${previewCustomer.firstName || ""}${
                            (previewCustomer.firstName && previewCustomer.lastName && " ") || ""
                          }${previewCustomer.lastName || ""}`
                        : "the new guest"
                    }:`}
                  </Typography>
                </Grid>
                <Grid container item spacing={2}>
                  <Grid item>
                    <FormControlLabel
                      control={<Checkbox color="primary" />}
                      label="Maintain Current Changes"
                      labelPlacement="end"
                      onChange={() => setPreserveRibbon(v => !v)}
                      checked={preserveRibbon}
                    />
                  </Grid>
                  {note.text && (
                    <Grid item>
                      <FormControlLabel
                        control={<Checkbox color="primary" />}
                        label="Maintain Current Note"
                        labelPlacement="end"
                        onChange={() => setPreserveNote(v => !v)}
                        checked={preserveNote}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            ))}
          {shouldConfirmEmail && (
            <>
              <Typography variant="body1">
                The lead email and selected guest email do not match, which one would you like to keep?
              </Typography>
              <RadioGroup
                onChange={(_event: ChangeEvent<HTMLInputElement>, value: string) => setOverrideEmail(value)}
                row
                value={overrideEmail}
              >
                <FormControlLabel
                  control={<Radio color="primary" />}
                  label={`Lead: ${lead.email}`}
                  value={lead.email}
                />
                <FormControlLabel
                  control={<Radio color="primary" />}
                  label={`Guest: ${previewCustomer?.email}`}
                  value={previewCustomer?.email}
                />
              </RadioGroup>
            </>
          )}
        </DialogContent>
      </Modal>
    </Paper>
  );
};

export default Search;
